import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

interface ServerVersion {
  version: string;
  buildDate: string;
  gitCommit: string;
  timestamp?: number;
}

interface VersionPollingConfig {
  pollIntervalMs?: number;
  enabled?: boolean;
}

export function useVersionPolling(config: VersionPollingConfig = {}) {
  const { pollIntervalMs = 30 * 60 * 1000, enabled = true } = config; // Default 30 minutes
  
  const [serverVersion, setServerVersion] = useState<ServerVersion | null>(null);
  const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
  const [lastChecked, setLastChecked] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Get current client version from build-time injection
  const currentClientVersion = __VITE_GIT_COMMIT_HASH__ || 'unknown';

  const checkVersion = useCallback(async () => {
    if (!enabled) return;

    try {
      const apiUrl = import.meta.env.MODE === 'production' 
        ? 'https://ams-admin-v2-server.amajormusic.co.uk'
        : 'http://localhost:3000';
      
      const response = await axios.get<ServerVersion>(`${apiUrl}/version`, {
        timeout: 10000, // 10 second timeout
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });

      const newServerVersion = response.data;
      setServerVersion(newServerVersion);
      setLastChecked(new Date());
      setError(null);

      // Compare versions - update available if server version differs from client
      const updateAvailable = newServerVersion.gitCommit !== currentClientVersion;
      setIsUpdateAvailable(updateAvailable);

      if (updateAvailable) {
        console.log('🔄 Update available:', {
          client: currentClientVersion,
          server: newServerVersion.gitCommit,
          buildDate: newServerVersion.buildDate
        });
      }

    } catch (err) {
      console.warn('Version check failed:', err);
      setError(err instanceof Error ? err.message : 'Version check failed');
    }
  }, [enabled, currentClientVersion]);

  // Initial check and polling setup
  useEffect(() => {
    if (!enabled) return;

    // Check immediately
    checkVersion();

    // Set up polling interval
    const interval = setInterval(checkVersion, pollIntervalMs);

    return () => clearInterval(interval);
  }, [checkVersion, pollIntervalMs, enabled]);

  // Manual check function
  const forceCheck = useCallback(() => {
    checkVersion();
  }, [checkVersion]);

  return {
    serverVersion,
    isUpdateAvailable,
    lastChecked,
    error,
    currentClientVersion,
    forceCheck,
  };
}